import type {
  CoreServices,
  Entity,
  RoomPartComponent,
  SceneManager,
  ViewerObjectComponent,
  WorldTransformComponent
} from '@inter-ikea-core/core';
import {
  AxisAlignedBox,
  ComponentIds,
  HierarchyUtils,
  RoomPartType,
  Vector
} from '@inter-ikea-core/core';
import { RoomDimensionOptionsComponent } from './Components/UserChoicesComponents/RoomDimensionOptionsComponent';
import { DefaultRoomSettings } from './helpers/room';
import type {
  IKvadrat
} from '@inter-ikea-kvadrat/kvadrat';
import { getDescendantWithRoom, hasKvadratComponent, hasRoomComponent, MeshType } from '@inter-ikea-kvadrat/kvadrat';
import { RenderSettings } from './helpers/renderSettings';
import { CORE_TO_VIEWER_SCALE } from 'utils/constants';

// Type definitions to avoid circular dependency with range package
export interface RoomDimensions {
  width: number;
  height: number;
  depth: number;
}

export interface RoomOptions {
  wallColor?: string;
  dimensions?: RoomDimensions;
  floorMaterial?: string;
}

export const getKvadratRoomEntity = (coreServices: CoreServices) => {
  return getDescendantWithRoom(coreServices.sceneManager.scene);
};

export const getOldRoomEntity = (sceneManager: SceneManager) => {
  return HierarchyUtils.findInHierarchy(
    sceneManager.scene,
    (it: Entity) => it.hasComponent(ComponentIds.ROOM_COMPONENT_ID) || hasRoomComponent(it),
  );
};

export const getSelectedRoomDimensions = (coreServices: CoreServices): RoomDimensions => {
  const roomDimensions = coreServices.sceneManager.scene.getComponent<RoomDimensionOptionsComponent>(RoomDimensionOptionsComponent.ID);

  return {
    width: roomDimensions?.width ?? DefaultRoomSettings.ROOM_WIDTH_M * CORE_TO_VIEWER_SCALE,
    height: roomDimensions?.height ?? DefaultRoomSettings.ROOM_HEIGHT_M * CORE_TO_VIEWER_SCALE,
    depth: roomDimensions?.depth ?? DefaultRoomSettings.ROOM_DEPTH_M * CORE_TO_VIEWER_SCALE
  };
};

export const getEffectiveRoomDimensions = (coreServices: CoreServices): RoomDimensions => {
  const roomDimensions = getRoomDimensions(coreServices);

  if (roomDimensions) {
    return {
      width: roomDimensions.x,
      height: roomDimensions.y,
      depth: roomDimensions.z
    };
  }

  return getSelectedRoomDimensions(coreServices);
};

export const getRoomViewerObjectIds = (coreServices: CoreServices) => {
  const roomEntity = getKvadratRoomEntity(coreServices);

  if (!roomEntity) {
    return [];
  }

  return [roomEntity, ...HierarchyUtils.descendants(roomEntity)]
    .map((entity) => {
      return entity.getComponent<ViewerObjectComponent>(ComponentIds.VIEWER_OBJECT_COMPONENT_ID)?.viewerObject.getViewerObjectId();
    })
    .filter((viewerObjectId): viewerObjectId is number => viewerObjectId !== undefined);
};

/**
 * Simple extractor function to get room geometry from Kvadrat connectors
 */
export const kvadratConnectorsToRoomGeometry = (kvadratConnectors: any) => {
  return kvadratConnectors.rooms[0];
};

/**
 * Calculates the center position of a room by analyzing its room parts.
 *
 * This method finds all room part entities in the scene and calculates the center point
 * based on their world positions. The center is determined by finding the midpoint
 * between the minimum and maximum coordinates of all room parts.
 *
 * @param coreServices - The Core services instance containing scene manager
 * @returns The center position of the room in meters, or null if no room parts are found
 *          Returns an object with x, y, z coordinates divided by 1000 (converting to meters)
 */
export const getRoomCenterPosition = (coreServices: CoreServices) => {
  const roomParts = HierarchyUtils.descendants(coreServices.sceneManager.scene).filter(entity =>
    entity.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID)
  );

  if (roomParts.length === 0) {
    return null;
  }

  let minX = Infinity,
    minY = Infinity,
    minZ = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity,
    maxZ = -Infinity;

  roomParts.forEach(roomPart => {
    const worldTransform = roomPart.getComponent<WorldTransformComponent>(
      ComponentIds.WORLD_TRANSFORM_COMPONENT_ID
    );
    const position = worldTransform?.transform.position;

    if (!position) return;

    // Update bounds considering negative values
    minX = Math.min(minX, position.X);
    minY = Math.min(minY, position.Y);
    minZ = Math.min(minZ, position.Z);
    maxX = Math.max(maxX, position.X);
    maxY = Math.max(maxY, position.Y);
    maxZ = Math.max(maxZ, position.Z);
  });

  if (minX === Infinity || minY === Infinity || minZ === Infinity) {
    return null;
  }

  const center = {
    x: minX + (maxX - minX) / 2,
    y: minY + (maxY - minY) / 2,
    z: minZ + (maxZ - minZ) / 2
  };

  const convertedCenter = {
    x: center.x / CORE_TO_VIEWER_SCALE,
    y: center.y / CORE_TO_VIEWER_SCALE,
    z: center.z / CORE_TO_VIEWER_SCALE
  };

  return convertedCenter;
};

/**
 * Gets the bounding box of the room by finding all corner entities
 *
 * @param coreServices - The Core services instance containing scene manager
 * @returns AxisAlignedBox representing the room size, or undefined if no corners found
 */
export const getRoomSize = (coreServices: CoreServices) => {
  const scene = coreServices.sceneManager.scene;
  const roomParts = HierarchyUtils.getDescendantsWithComponent(
    scene,
    ComponentIds.ROOM_PART_COMPONENT_ID
  );

  if (!roomParts) return;

  const cornerPartsTransforms = roomParts
    .filter(
      (r: Entity) =>
        r.getComponent<RoomPartComponent>(ComponentIds.ROOM_PART_COMPONENT_ID)?.type ===
        RoomPartType.CORNER
    )
    .map(
      (r: Entity) =>
        r.getComponent<WorldTransformComponent>(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
          ?.transform.position
    )
    .filter(Boolean) as Vector[];

  if (cornerPartsTransforms.length === 0) return;

  return AxisAlignedBox.fromVertices(cornerPartsTransforms);
};

/**
 * Gets the room dimensions (width, height, depth) from the bounding box
 * Will be removed in the future when we have more complex room structures
 *
 * @param coreServices - The Core services instance containing scene manager
 * @returns Object with x, y, z dimensions or undefined if no room size found
 */
export const getRoomDimensions = (coreServices: CoreServices) => {
  const size = getRoomSize(coreServices);

  if (!size) return;

  const dimensions = {
    x: Math.abs(size.min.X - size.max.X),
    y: Math.abs(size.min.Y - size.max.Y),
    z: Math.abs(size.min.Z - size.max.Z)
  };

  return dimensions;
};

/**
 * Manually applies materials to room surfaces (floor, walls, ceiling).
 * This is the standard approach for material application in Kvadrat v14+
 * and should be called after room creation.
 *
 * @param kvadrat - The Kvadrat instance
 * @param roomMaterials - Array of material configurations for different room surfaces
 */
export function applyMaterialsManually(
  kvadrat: IKvadrat,
  roomMaterials: RenderSettings['roomMaterials']
) {
  const floorMaterial = roomMaterials.find(rm => rm.id === MeshType.FLOOR);
  const wallMaterial = roomMaterials.find(rm => rm.id === MeshType.WALL);
  const ceilingMaterial = roomMaterials.find(rm => rm.id === MeshType.CEILING);

  if (floorMaterial) {
    kvadrat.applyMaterialToAllFloors(floorMaterial as any);
  }

  if (wallMaterial) {
    kvadrat.applyMaterialToAllWalls(wallMaterial as any);
  }

  if (ceilingMaterial) {
    kvadrat.applyMaterialToAllCeilings(ceilingMaterial as any);
  }
}

/** Returns all entities that are direct children of the room or any of the room parts, such as things attached to doors */
export function getTopLevelEntitiesInRoom(sceneManager: SceneManager) {
  return sceneManager.entities.filter((it) => {
    const parent = HierarchyUtils.parent(it);
    return parent && isPartOfRoom(parent) && !isPartOfRoom(it);
  });
}

/**
 * Check if entity is part of the room, for example wall, floor, window, door, obstacle.
 * @param entity
 * @returns true if the entity is part of room.
 */
export const isPartOfRoom = (entity: Entity) =>
  entity.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID) || hasKvadratComponent(entity);
